import { Button, Position } from '@consigli/facade';
import {
  useBuildingPackageId,
  useProjectId,
  useLazyGetBuildingPackageBuildingJsonQuery,
  useUpdateBuildingPackageBuildingJsonMutation,
} from '@consigli/hooks';
import { type BuildingJSONData } from '@consigli/types';
import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';

import {
  BuildingDesignCriteria as BuildingDesignCriteriaComponent,
  CriteriaItem,
} from './building-design-criteria';
import {
  BuildingDesignCriteria,
  BuildingJsonData,
  CRITERIA_CONFIGS,
  getCriteriaFromBuildingData,
} from './types';

interface BuildingLevelOverviewProps {
  onContinue?: () => void;
  isLoading?: boolean;
}

export const BuildingLevelOverview: FC<BuildingLevelOverviewProps> = ({
  onContinue,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const buildingPackageId = useBuildingPackageId();
  const [buildingJson, setBuildingJson] = useState<BuildingJSONData | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [getBuildingJson] = useLazyGetBuildingPackageBuildingJsonQuery();
  const [updateBuildingPackageBuildingJson] = useUpdateBuildingPackageBuildingJsonMutation();
  const [fullBuildingData, setFullBuildingData] = useState<BuildingJsonData | null>(null);

  const criteriaFromBuilding = useMemo(() => {
    if (!buildingJson) return [];

    try {
      const buildingData: BuildingJsonData = buildingJson as BuildingJsonData;
      return getCriteriaFromBuildingData(buildingData.building_design_criteria, t);
    } catch (error) {
      console.error('Error parsing building JSON:', error);
      return [];
    }
  }, [buildingJson, t]);

  const [currentCriteria, setCurrentCriteria] = useState<CriteriaItem[]>([]);

  const mapCriteriaToBuildingDesignCriteria = useCallback(
    (criteria: CriteriaItem[]): BuildingDesignCriteria | null => {
      if (!fullBuildingData) return null;

      const updatedParams = { ...fullBuildingData.building_design_criteria };

      criteria.forEach((item) => {
        const config = CRITERIA_CONFIGS.find((c) => c.id === item.id);

        if (!config || !config.isNumeric) return;

        const displayValue = parseFloat(String(item.value));
        const jsonValue = config.fromDisplay ? config.fromDisplay(displayValue) : displayValue;
        (updatedParams as Record<string, unknown>)[config.propertyKey] = jsonValue;
      });

      return updatedParams;
    },
    [fullBuildingData],
  );

  const fetchBuildingJson = useCallback(async () => {
    try {
      const result = await getBuildingJson({
        projectId,
        buildingPackageId,
      }).unwrap();
      setBuildingJson(result);

      if (result) {
        const parsed: BuildingJsonData = result as BuildingJsonData;
        setFullBuildingData(parsed);
      }

      return result;
    } catch (error) {
      console.error('Failed to fetch building JSON:', error);
      return null;
    }
  }, [getBuildingJson, projectId, buildingPackageId]);

  useEffect(() => {
    if (projectId && buildingPackageId) {
      fetchBuildingJson();
    }
  }, [fetchBuildingJson, projectId, buildingPackageId]);

  useEffect(() => {
    if (buildingJson && criteriaFromBuilding.length > 0) {
      setCurrentCriteria(criteriaFromBuilding);
    }
  }, [criteriaFromBuilding, buildingJson]);

  const handleApproveCriteria = useCallback(
    async (finalCriteria?: CriteriaItem[]) => {
      setIsApproving(true);
      try {
        // Map the current criteria back to building design criteria
        const source = finalCriteria ?? currentCriteria;
        const updatedBuildingDesignCriteria = mapCriteriaToBuildingDesignCriteria(source);

        if (fullBuildingData && updatedBuildingDesignCriteria) {
          // Create the final building JSON with updated building design criteria
          const updatedBuildingJson: BuildingJsonData = {
            ...fullBuildingData,
            building_design_criteria: updatedBuildingDesignCriteria,
          };

          await updateBuildingPackageBuildingJson({
            projectId,
            buildingPackageId,
            data: updatedBuildingJson,
          });

          setFullBuildingData(updatedBuildingJson);

          toast.success(t('building-design-criteria.status.approved-success'));
        } else {
          throw new Error('Building data not available for update');
        }
      } catch (error) {
        console.error(t('building-design-criteria.errors.approving-failed'), error);
        toast.error(t('building-design-criteria.errors.approving-failed'));
      } finally {
        setIsApproving(false);
      }
    },
    [
      t,
      fullBuildingData,
      currentCriteria,
      mapCriteriaToBuildingDesignCriteria,
      updateBuildingPackageBuildingJson,
      projectId,
      buildingPackageId,
    ],
  );

  const handleCriteriaChange = useCallback((updatedCriteria: CriteriaItem[]) => {
    setCurrentCriteria(updatedCriteria);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('building-design-criteria.page-title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('building-design-criteria.page-description')}
          </p>
        </div>

        <div className="mb-8">
          {currentCriteria.length > 0 ? (
            <BuildingDesignCriteriaComponent
              criteria={currentCriteria}
              onApprove={handleApproveCriteria}
              onCriteriaChange={handleCriteriaChange}
              isApproving={isApproving}
              showApproveButton={true}
            />
          ) : (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('building-design-criteria.loading')}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center mb-8">
          <Button
            onClick={onContinue}
            primary
            rounded
            icon={FiArrowRight}
            iconPosition={Position.RIGHT}
            className="px-6 py-3 text-base font-medium"
            disabled={isLoading || currentCriteria.length === 0}
          >
            {t('building-design-criteria.actions.continue')}
          </Button>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            {t('building-design-criteria.status.footer-info')}
          </p>
        </div>
      </div>
    </div>
  );
};
